import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Phone, User as UserIcon, Calendar, Package, Inbox, BadgeCheck, BadgeX, MessageSquare, Save, Edit3 } from 'lucide-react';
import EditMemberModal from './EditMemberModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
};

const CustomerProfileView = () => {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  const load = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/customers/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setNotes(res.data?.user?.admin_notes || '');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load customer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [userId]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(`${API}/admin/customers/${userId}`, { admin_notes: notes }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      toast.success('Notes saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) return <p className="text-maroon/60">Loading…</p>;
  if (!data?.user) {
    return (
      <div data-testid="customer-profile-not-found">
        <Link to="/admin/leads" className="text-maroon/60 hover:text-maroon inline-flex items-center gap-1 mb-4">
          <ArrowLeft size={14} /> Back to Leads
        </Link>
        <p className="text-maroon/60">Customer not found.</p>
      </div>
    );
  }

  const { user, profile, bookings, leads, consultations } = data;

  return (
    <div data-testid="customer-profile-view">
      <Link to="/admin/members" className="text-maroon/60 hover:text-maroon inline-flex items-center gap-1 mb-4 text-sm" data-testid="customer-profile-back">
        <ArrowLeft size={14} /> Back to Members
      </Link>

      <div className="card mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <UserIcon className="text-terracotta" size={22} />
              <h2 className="text-2xl font-heading font-bold text-maroon">{user.name}</h2>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-bold border bg-emerald-500/15 text-emerald-700 border-emerald-500/30">
                <BadgeCheck size={12} /> Member
              </span>
              {user.is_active === false && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-bold border bg-coral/15 text-coral border-coral/30">
                  <BadgeX size={12} /> Inactive
                </span>
              )}
              {user.newsletter_subscribed && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-bold border bg-terracotta/15 text-terracotta border-terracotta/30">
                  <Mail size={12} /> Newsletter
                </span>
              )}
            </div>
            <p className="text-xs text-maroon/40">Member since {formatDate(user.created_at)}</p>
          </div>
          <button onClick={() => setEditing(true)} className="btn-primary inline-flex items-center gap-2" data-testid="customer-profile-edit-btn">
            <Edit3 size={16} /> Edit Member
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-maroon/80">
          <div className="flex items-center gap-2">
            <Mail size={14} className="text-maroon/40" />
            <a href={`mailto:${user.email}`} className="hover:text-terracotta">{user.email}</a>
          </div>
          {(user.phone || profile?.phone) && (
            <div className="flex items-center gap-2">
              <Phone size={14} className="text-maroon/40" />
              <a href={`tel:${user.phone || profile.phone}`} className="hover:text-terracotta">{user.phone || profile.phone}</a>
            </div>
          )}
        </div>
      </div>

      {/* Admin-only notes — never shown to the customer */}
      <div className="card mb-6 bg-coral/5 border-coral/20" data-testid="customer-profile-admin-notes">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h3 className="text-lg font-heading font-bold text-maroon flex items-center gap-2">
            <MessageSquare size={18} className="text-coral" />
            Customer Service Notes
            <span className="text-xs font-normal text-coral">— admin-only, never shown to the customer</span>
          </h3>
          <button onClick={saveNotes} disabled={savingNotes} className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid="customer-profile-notes-save-btn">
            <Save size={12} /> {savingNotes ? 'Saving…' : 'Save'}
          </button>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Anything the customer mentioned on a call, special considerations, VIP status, etc."
          className="w-full bg-white border-2 border-coral/20 focus:border-coral focus:ring-0 rounded-sm p-3 text-sm resize-none"
          data-testid="customer-profile-notes-textarea"
        />
      </div>

      {profile && (
        <div className="card mb-6" data-testid="customer-profile-traveler">
          <h3 className="text-lg font-heading font-bold text-maroon mb-3">Traveler Profile</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-maroon/80">
            <div><strong>Hotel budget:</strong> {profile.hotel_budget || '—'}</div>
            <div><strong>Date of birth:</strong> {profile.date_of_birth || '—'}</div>
            <div><strong>Experience types:</strong> {(profile.experience_types || []).join(', ') || '—'}</div>
            <div><strong>Top priorities:</strong> {(profile.top_priorities || []).join(', ') || '—'}</div>
            <div><strong>Planning scope:</strong> {(profile.planning_scope || []).join(', ') || '—'}</div>
            <div><strong>Dietary:</strong> {profile.dietary_requirements || '—'}</div>
            <div><strong>Allergies:</strong> {profile.allergies || '—'}</div>
            <div><strong>Emergency contact:</strong> {profile.emergency_contact_name || '—'} {profile.emergency_contact_phone ? `(${profile.emergency_contact_phone})` : ''}</div>
            {profile.past_trips_loved && (
              <div className="sm:col-span-2"><strong>Past trips loved:</strong> {profile.past_trips_loved}</div>
            )}
            {profile.favorite_hotels && (
              <div className="sm:col-span-2"><strong>Favorite hotels:</strong> {profile.favorite_hotels}</div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card" data-testid="customer-profile-bookings">
          <h3 className="text-lg font-heading font-bold text-maroon mb-3 flex items-center gap-2">
            <Package size={18} className="text-terracotta" /> Bookings ({bookings?.length || 0})
          </h3>
          {(!bookings || bookings.length === 0) ? (
            <p className="text-sm text-maroon/50">No bookings yet.</p>
          ) : (
            <ul className="space-y-2">
              {bookings.map((b) => (
                <li key={b.id} className="text-sm border border-maroon/10 rounded-sm p-2 bg-sand/20">
                  <div className="font-semibold text-maroon">{b.package_title}</div>
                  <div className="text-maroon/60 text-xs flex items-center gap-2 flex-wrap">
                    <Calendar size={12} /> {formatDate(b.created_at)}
                    <span className="px-1.5 py-0.5 bg-maroon/10 rounded">${b.deposit_amount}</span>
                    <span className="px-1.5 py-0.5 bg-maroon/10 rounded">{b.payment_status}</span>
                    <span className="px-1.5 py-0.5 bg-maroon/10 rounded">{b.booking_status}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card" data-testid="customer-profile-leads">
          <h3 className="text-lg font-heading font-bold text-maroon mb-3 flex items-center gap-2">
            <Inbox size={18} className="text-terracotta" /> Quote Leads ({leads?.length || 0})
          </h3>
          {(!leads || leads.length === 0) ? (
            <p className="text-sm text-maroon/50">No quote requests yet.</p>
          ) : (
            <ul className="space-y-2">
              {leads.map((l) => (
                <li key={l.id} className="text-sm border border-maroon/10 rounded-sm p-2 bg-sand/20">
                  <div className="font-semibold text-maroon">{l.package_title || 'General inquiry'}</div>
                  <div className="text-maroon/60 text-xs flex items-center gap-2 flex-wrap">
                    <Calendar size={12} /> {formatDate(l.created_at)}
                    <span className="px-1.5 py-0.5 bg-maroon/10 rounded">{l.status}</span>
                    {l.travel_dates && <span>· {l.travel_dates}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Consultations (matched by email) */}
      <div className="card mt-6" data-testid="customer-profile-consultations">
        <h3 className="text-lg font-heading font-bold text-maroon mb-3 flex items-center gap-2">
          <MessageSquare size={18} className="text-terracotta" /> Consultations ({consultations?.length || 0})
        </h3>
        {(!consultations || consultations.length === 0) ? (
          <p className="text-sm text-maroon/50">No consultation requests yet.</p>
        ) : (
          <ul className="space-y-2">
            {consultations.map((c) => (
              <li key={c.id} className="text-sm border border-maroon/10 rounded-sm p-3 bg-sand/20">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold text-maroon">{c.destination_interest}</span>
                  <span className="px-1.5 py-0.5 bg-maroon/10 rounded text-xs">{c.status}</span>
                  <span className="text-xs text-maroon/50">{formatDate(c.created_at)}</span>
                </div>
                <div className="text-xs text-maroon/60 flex gap-3 flex-wrap">
                  {c.preferred_dates && <span>📅 {c.preferred_dates}</span>}
                  <span>👥 {c.group_size} traveler{c.group_size === 1 ? '' : 's'}</span>
                </div>
                {c.message && <p className="text-xs text-maroon/70 mt-2 whitespace-pre-line">{c.message}</p>}
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <EditMemberModal
          userId={userId}
          onClose={() => setEditing(false)}
          onSaved={(updated) => { setData(updated); setNotes(updated?.user?.admin_notes || ''); setEditing(false); toast.success('Member updated'); }}
        />
      )}
    </div>
  );
};

export default CustomerProfileView;
